import { useCallback } from 'react';
import cn from 'clsx';
import { useHasNoLocation } from '@pkgs/components/hooks/useHasNoLocation';
import { useCreateSearchAlertButtonClick } from '@pkgs/components/hooks/useCreateSearchAlertButtonClick';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { SKY_IMAGE_URL } from '@pkgs/components/consts/consts';
import { SearchAlertTooltip } from '@pkgs/components/components/SearchAlert/SearchAlertTooltip';
import BellPlus from '@pkgs/components/assets/icons/small/BellPlus.svg';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';

import styles from './SearchAlertCtaCard.module.css';

export const SEARCH_ALERT_CTA_ELEMENT_ID = 'tile_secondary_search-alert';

type SearchAlertCtaCardProps = {
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

export const SearchAlertCtaCard = ({ className, onClick }: SearchAlertCtaCardProps) => {
  const { t, ImageComponent } = useAppContext();
  const { trackButtonClickUserInteractionEvent } = useCreateSearchAlertButtonClick();

  const hasNoLocation = useHasNoLocation();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      trackButtonClickUserInteractionEvent(SEARCH_ALERT_CTA_ELEMENT_ID);
    },
    [onClick, trackButtonClickUserInteractionEvent],
  );

  return (
    <article className={cn(styles.article, className)} data-testid="search-components_cta-card_search-alert">
      <ImageComponent src={SKY_IMAGE_URL} alt="Sky" width={120} height={99} />
      <div className={styles.textContainer}>
        <Headline className={styles.title} label={t('search.searchAlert.ctaCard.title')} size="h5" />
        <Copy text={t('search.searchAlert.ctaCard.text')} />
      </div>

      <SearchAlertTooltip side="top">
        <Button
          className={styles.button}
          icon={<BellPlus />}
          size="medium"
          variant="primary"
          label={t('search.searchAlert.ctaCard.button')}
          disabled={hasNoLocation}
          onClick={handleClick}
          testId="search-components_cta-card_search-alert_button"
        />
      </SearchAlertTooltip>
    </article>
  );
};
